import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Custom error handler that detects chunk loading failures and automatically reloads the page.
 * This handles cases where users have cached old versions of the app and new deployments
 * have generated new chunk files with different hashes.
 */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
	private hasReloadedOnce = false;

	constructor(private sentryErrorHandler: ErrorHandler) {}

	handleError(error: Error | any): void {
		const chunkFailedMessage = /error loading dynamically imported module/i;
		const chunkLoadError = /ChunkLoadError/i;
		const failedFetch = /Failed to fetch/i;

		// Check if it's a chunk loading error
		if (
			error &&
			(chunkFailedMessage.test(error.message) || chunkLoadError.test(error.message) || (failedFetch.test(error.message) && error.message.includes('.js')))
		) {
			// Only reload once to prevent infinite reload loop
			if (!this.hasReloadedOnce) {
				this.hasReloadedOnce = true;
				console.warn('Chunk loading failed. Reloading page to fetch latest version...', error);
				// Use window.location.reload() to force a full page reload and fetch latest chunks
				window.location.reload();
				return;
			} else {
				console.error('Chunk loading failed even after reload. Not reloading again.', error);
			}
		}

		// Delegate to Sentry error handler for all other errors
		this.sentryErrorHandler.handleError(error);
	}
}
